import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import * as GroupActions from './group.actions';
import { selectGroupState } from './group.selectors';
import { mergeMap, map, catchError, filter, of, switchMap, tap, concatMap } from 'rxjs';
import { GroupService } from "@core/services/group.service";
import { GroupStrandsPupilsError } from "@core/models/domain/group.domain";
import { extractErrorMessage } from "@core/utils/error-message.util";
import { Router } from '@angular/router';

// How long a successful load is considered fresh before loadGroupsIfNeeded refetches.
const GROUPS_CACHE_TTL_MS = 60_000;

@Injectable()
export class GroupEffects {
  constructor(
    private actions$: Actions,
    private groupService: GroupService,
    private store: Store,
    private router: Router
  ) {}

  // Store-first guard: skip the fetch when a load is already in flight or the cache is fresh.
  loadGroupsIfNeeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupActions.loadGroupsIfNeeded),
      concatLatestFrom(() => this.store.select(selectGroupState)),
      filter(([, state]) => !state.loading
        && (!state.loaded || Date.now() - (state.lastLoadedAt ?? 0) > GROUPS_CACHE_TTL_MS)),
      map(() => GroupActions.loadGroups())
    )
  );

  // LOAD GROUPS
  loadGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupActions.loadGroups),
      switchMap(() =>
        this.groupService.loadAllGroups().pipe(
          map((groups) => GroupActions.loadGroupsSuccess({ groups })),
          catchError((error) => {
            console.error('Error loading groups:', error);
            // A string, never the raw HttpErrorResponse: the page prints it (was "[object Object]").
            return of(GroupActions.loadGroupsFailure({ error: extractErrorMessage(error) }));
          })
        )
      )
    )
  );

  // CREATE GROUP
  createGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupActions.createGroup),
      mergeMap(({ request }) =>
        this.groupService.createGroup(request).pipe(
          map((created) => GroupActions.createGroupSuccess({ group: created })),
          catchError((error) => {
            console.error('Error creating group:', error);
            return of(GroupActions.createGroupFailure({ error: extractErrorMessage(error) }));
          })
        )
      )
    )
  );

  // UPDATE GROUP
  updateGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupActions.updateGroup),
      mergeMap(({ request }) =>
        this.groupService.updateGroup(request).pipe(
          map((updated) => {
            if (updated) {
              return GroupActions.updateGroupSuccess({ group: updated });
            } else {
              return GroupActions.updateGroupFailure({error: "Update failed",});
            }
          }),
          catchError((error) => {
            console.error('Error updating group:', error);
            return of(GroupActions.updateGroupFailure({ error: extractErrorMessage(error) }));
          })
        )
      )
    )
  );

  // REMOVE GROUP
  deleteGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupActions.deleteGroup),
      mergeMap(({ groupId, acknowledgeStrandedPupils }) =>
        this.groupService.deleteGroup(groupId, acknowledgeStrandedPupils ?? false).pipe(
          map((deletedId) =>
            GroupActions.deleteGroupSuccess({ groupId: deletedId })
          ),
          catchError((error) => {
            if (error instanceof GroupStrandsPupilsError) {
              return of(GroupActions.deleteGroupBlocked({ groupId: error.groupId, pupils: error.pupils }));
            }
            console.error('Error deleting group:', error);
            return of(GroupActions.deleteGroupFailure({ error: extractErrorMessage(error) }));
          })
        )
      )
    )
  );
}